import { useState, useRef, useCallback, useEffect } from 'react';
import { ChevronDown, ChevronRight, Plus, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import type { WbsTreeNode } from './types';
import type { ProjectMember } from '../../services/projectService';
import Avatar from '../common/Avatar';
import { toInputDate, formatShortDate, formatYYMMDD, DEPTH_ROW_BG, DEPTH_ROW_HOVER } from './wbsUtils';

interface Props {
  node: WbsTreeNode;
  isExpanded: boolean;
  hasChildren: boolean;
  bufferEnabled: boolean;
  members: ProjectMember[];
  titleColWidth: number;
  isHovered: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onToggle: () => void;
  onFieldUpdate: (wbsItemId: string, field: string, value: unknown) => void;
  onStartInlineCreate: (parentId: string, depth: number) => void;
  onDelete: (wbsItemId: string, title: string, childCount: number) => void;
}

export default function WbsTableRow({
  node,
  isExpanded,
  hasChildren,
  bufferEnabled,
  members,
  titleColWidth,
  isHovered,
  onMouseEnter,
  onMouseLeave,
  onToggle,
  onFieldUpdate,
  onStartInlineCreate,
  onDelete,
}: Props) {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; openUp: boolean } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const openMenu = useCallback(() => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const menuHeight = 80; // 메뉴 대략적인 높이
      const openUp = rect.bottom + menuHeight > window.innerHeight;
      setMenuPos({
        top: openUp ? rect.top - menuHeight : rect.bottom + 4,
        left: rect.right - 128, // w-32 = 128px
        openUp,
      });
    }
    setMenuOpen(true);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const handleScroll = () => setMenuOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [menuOpen]);

  const is1depth = node.depth === 1;
  const canAddChild = node.depth < 3;
  const rowBg = isHovered ? (DEPTH_ROW_HOVER[node.depth] || '') : (DEPTH_ROW_BG[node.depth] || '');
  const indent = (node.depth - 1) * 24;
  const numberPrefix = node.depth <= 2 ? `${node.sortOrder + 1}. ` : '';

  const childCount = node.children?.length ?? 0;

  return (
    <div
      className={`group flex items-center border-b text-sm ${rowBg}`}
      style={{ height: 33 }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* 항목명 */}
      <div
        className="px-2 py-1.5 border-r flex items-center gap-1"
        style={{ width: titleColWidth, minWidth: titleColWidth, maxWidth: titleColWidth, paddingLeft: `${indent + 8}px` }}
      >
        {/* 토글 */}
        {hasChildren ? (
          <button onClick={onToggle} className="w-4 h-4 flex-shrink-0 text-gray-500">
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        ) : (
          <span className="w-4 flex-shrink-0" />
        )}

        {/* 제목 */}
        {editingField === 'title' ? (
          <input
            autoFocus
            defaultValue={node.title}
            onBlur={(e) => {
              if (e.target.value.trim()) onFieldUpdate(node.wbsItemId, 'title', e.target.value.trim());
              setEditingField(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (e.currentTarget.value.trim()) onFieldUpdate(node.wbsItemId, 'title', e.currentTarget.value.trim());
                setEditingField(null);
              }
              if (e.key === 'Escape') setEditingField(null);
            }}
            className="flex-1 text-sm border rounded px-1 py-0.5 min-w-0"
          />
        ) : (
          <span
            className={`flex-1 truncate cursor-default ${is1depth ? 'font-medium' : ''}`}
            onDoubleClick={() => setEditingField('title')}
          >
            {numberPrefix}{node.title}
          </span>
        )}

        {/* [+] 하위 추가 */}
        {canAddChild && (
          <button
            onClick={() => onStartInlineCreate(node.wbsItemId, node.depth + 1)}
            className="w-4 h-4 flex-shrink-0 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-blue-600 transition-opacity"
          >
            <Plus size={12} />
          </button>
        )}

        {/* 더보기 메뉴 */}
        <div className="relative" ref={menuRef}>
          <button
            ref={btnRef}
            onClick={() => menuOpen ? setMenuOpen(false) : openMenu()}
            className="w-4 h-4 flex-shrink-0 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600 transition-opacity"
          >
            <MoreVertical size={12} />
          </button>
          {menuOpen && menuPos && (
            <>
              <div className="fixed inset-0 z-[9998]" onClick={() => setMenuOpen(false)} />
              <div
                className="fixed z-[9999] bg-white border rounded-lg shadow-lg py-1 w-32"
                style={{ top: menuPos.top, left: menuPos.left }}
              >
                <button
                  onClick={() => { setEditingField('title'); setMenuOpen(false); }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Pencil size={12} /> 수정
                </button>
                <button
                  onClick={() => { onDelete(node.wbsItemId, node.title, childCount); setMenuOpen(false); }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
                >
                  <Trash2 size={12} /> 삭제
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* 담당자 */}
      <div className="group/assignee w-[60px] min-w-[60px] px-1 py-1 border-r relative">
        <div className="flex items-center justify-center h-full pointer-events-none">
          {node.assignee ? (
            <Avatar
              size="xs"
              userId={node.assignee.id}
              name={node.assignee.name}
              avatarUrl={node.assignee.avatarUrl}
            />
          ) : (
            <span className="text-sm text-gray-300">-</span>
          )}
        </div>
        <select
          value={node.assigneeId ?? ''}
          onChange={(e) => onFieldUpdate(node.wbsItemId, 'assigneeId', e.target.value || null)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        >
          <option value="">미할당</option>
          {members.map((m) => (
            <option key={m.userId} value={m.userId}>
              {m.user.name}
            </option>
          ))}
        </select>
        <div className="pointer-events-none absolute left-1/2 top-full -translate-x-1/2 mt-1 z-50 hidden group-hover/assignee:block whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white shadow-lg">
          {node.assignee?.name ?? '미할당'}
        </div>
      </div>

      {/* 시작일 */}
      <div className="w-[92px] min-w-[92px] px-1 py-1 border-r text-center relative">
        <span className="text-sm text-gray-700 pointer-events-none whitespace-nowrap">
          {formatYYMMDD(node.startDate)}
        </span>
        <input
          type="date"
          value={toInputDate(node.startDate)}
          onChange={(e) => onFieldUpdate(node.wbsItemId, 'startDate', e.target.value || null)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0"
        />
      </div>

      {/* 종료일 */}
      <div className="w-[92px] min-w-[92px] px-1 py-1 border-r text-center relative">
        <span className="text-sm text-gray-700 pointer-events-none whitespace-nowrap">
          {formatYYMMDD(node.endDate)}
        </span>
        <input
          type="date"
          value={toInputDate(node.endDate)}
          onChange={(e) => onFieldUpdate(node.wbsItemId, 'endDate', e.target.value || null)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0"
        />
      </div>

      {/* 수행기간 */}
      <div className="w-[50px] min-w-[50px] px-1 py-1.5 border-r text-center text-sm text-gray-600">
        {node.computed?.workingDays ?? '-'}
      </div>

      {/* 휴일 */}
      <div className="w-[50px] min-w-[50px] px-1 py-1.5 border-r text-center text-sm text-gray-400">
        {node.computed?.holidays ?? '-'}
      </div>

      {/* 보정 */}
      <div className="w-[50px] min-w-[50px] px-1 py-1 border-r text-center">
        <input
          type="number"
          value={node.adjustment}
          onChange={(e) => onFieldUpdate(node.wbsItemId, 'adjustment', Number(e.target.value))}
          className="w-full text-center text-sm border-0 bg-transparent focus:ring-1 focus:ring-blue-300 rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0"
        />
      </div>

      {/* 버퍼기간 (1depth, 2depth) */}
      {bufferEnabled && (
        <div className="w-[50px] min-w-[50px] px-1 py-1.5 border-r text-center text-sm text-gray-600">
          {node.depth <= 2 ? (node.computed?.bufferDays ?? '-') : ''}
        </div>
      )}

      {/* 예상지연 (1depth만) */}
      {bufferEnabled && (
        <div className="w-[80px] min-w-[80px] px-1 py-1.5 text-center text-sm">
          {node.depth === 1 && node.computed?.expectedDelay ? (
            <span className="text-red-500 font-medium">
              {formatShortDate(node.computed.expectedDelay)}
            </span>
          ) : ''}
        </div>
      )}
    </div>
  );
}
